import React, { useMemo, useState } from 'react'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import {
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBBtn,
  MDBModalFooter,
  MDBModalDialog,
  MDBModalContent,
} from 'mdb-react-ui-kit'

import LoadingSpinner from './../../../../components/shared/LoadingSpinner.component'
import { getHeadshot } from './../../../../shared/utilities/getHeadshot.function'
import {
  getRaceKeyFromMilestone,
  getRaceThresholdForMilestone,
} from './../../../../constants/race-to-the-ring.constants'
import Store from './../../store'

import './WinnerSpotlight.component.scss'

const DEFAULT_LIMIT = 6

const getNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatPoints = value => `${getNumber(value).toLocaleString()} pts`

const formatRaceLabel = raceKey => {
  if (!raceKey) return 'Agent'
  return raceKey
    .split(/[\s-_]/)
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
}

const getMilestoneValue = (milestone, key) => {
  if (!milestone) return undefined
  if (typeof milestone.get === 'function') {
    const value = milestone.get(key)
    if (typeof value !== 'undefined') return value
  }
  if (typeof milestone.raw === 'function') {
    const raw = milestone.raw()
    if (raw && Object.prototype.hasOwnProperty.call(raw, key)) return raw[key]
  }
  if (typeof milestone === 'object' && milestone !== null) {
    return milestone[key]
  }
  return undefined
}

const getWinnerName = milestone => {
  const first = getMilestoneValue(milestone, 'mi_fname') ?? ''
  const last = getMilestoneValue(milestone, 'mi_lname') ?? ''
  const name = `${first} ${last}`.trim()
  return name || 'TBD'
}

const getWinnerHeadshot = milestone => {
  const userId = getMilestoneValue(milestone, 'user_id')
  const user = Store.getUserById(userId)
  const userPicture =
    (typeof user?.get === 'function' && user.get('u_picture')) ??
    (typeof user?.raw === 'function' ? user.raw()?.u_picture : undefined)
  const milestonePicture =
    getMilestoneValue(milestone, 'u_picture') ??
    getMilestoneValue(milestone, 'pic') ??
    getMilestoneValue(milestone, 'image')

  return getHeadshot({
    u_picture: userPicture ?? milestonePicture,
    id: userId,
  })
}

const getFullBio = (recipient, index) => {
  const bio = recipient?.get?.('ring_bio')
  return bio && bio.trim().length > 0 ? bio.trim() : ''
}

const getBioPreview = bio => {
  if (!bio) return 'Biography coming soon.'
  const trimmed = bio.trim()
  const firstSentenceMatch = trimmed.match(/.+?[.!?](\s|$)/)
  const firstSentence = firstSentenceMatch ? firstSentenceMatch[0].trim() : trimmed
  const cappedSentence = firstSentence.length > 100 ? `${firstSentence.slice(0, 100).trim()}...` : firstSentence

  if (firstSentenceMatch) return cappedSentence
  return trimmed.length > 100 ? `${trimmed.slice(0, 100).trim()}...` : trimmed
}

const formatWinDate = milestone => {
  const dateValue = getMilestoneValue(milestone, 'mi_date')
  return dateValue ? moment(dateValue).format('MMM D, YYYY') : 'Date unavailable'
}

const WinnerSpotlight = () => {
  const [activeWinner, setActiveWinner] = useState(null)

  const winners = useMemo(() => (Array.isArray(Store?.Winners?.all) ? Store.Winners.all : []), [Store.Winners.all])

  const orderedWinners = useMemo(
    () =>
      winners.slice().sort((a, b) => {
        const aDate = new Date(getMilestoneValue(a, 'mi_date') || 0).getTime()
        const bDate = new Date(getMilestoneValue(b, 'mi_date') || 0).getTime()
        return bDate - aDate
      }),
    [winners]
  )

  if (Store.isLoading) {
    return (
      <div id='WinnerSpotlightComponent'>
        <LoadingSpinner size='md' isActive={true} label='Loading Race to the Ring data' />
      </div>
    )
  }

  if (!orderedWinners.length) {
    return (
      <section id='WinnerSpotlightComponent' className='winner-spotlight'>
        <div className='winner-spotlight__header'>
          <h1 className='winner-spotlight__winners'>Royal Ring Inductees!</h1>
          <h2 className='winner-spotlight__title'>Statistics & Bios</h2>
        </div>
        <p className='winner-spotlight__empty'>Once new Royal Ring biographies are available, they'll shine here.</p>
      </section>
    )
  }

  const handleOpen = payload => setActiveWinner(payload)
  const handleClose = () => setActiveWinner(null)

  return (
    <section id='WinnerSpotlightComponent' className='winner-spotlight'>
      <div className='winner-spotlight__header'>
        <h1 className='winner-spotlight__winners'>Royal Ring Inductees!</h1>
        <h2 className='winner-spotlight__title'>Statistics & Bios</h2>
      </div>

      <div className='winner-spotlight__cards'>
        {orderedWinners.map((winner, index) => {
          const raceKey = getRaceKeyFromMilestone(winner)
          const raceLabel = formatRaceLabel(raceKey)
          const userId = getMilestoneValue(winner, 'user_id')
          const recipient = Store.getRecipientByUserId(userId, raceKey)
          const threshold = recipient?.get?.('ring_threshold') ?? getRaceThresholdForMilestone(winner)
          const winDate = formatWinDate(winner)
          const headshot = getWinnerHeadshot(winner)
          const fullBio = getFullBio(recipient, index)
          const preview = getBioPreview(fullBio)

          const modalPayload = {
            winner,
            raceLabel,
            headshot,
            threshold: getNumber(threshold),
            winDate,
            fullBio,
          }

          return (
            <button
              type='button'
              key={winner?.get?.('milestone_id') ?? `${userId}-${winDate}`}
              className={`winner-spotlight__card winner-spotlight__card--${raceKey}`}
              onClick={() => handleOpen(modalPayload)}
            >
              <div className='winner-spotlight__card-body'>
                <img src={headshot} alt={getWinnerName(winner)} className='winner-spotlight__avatar' />
                <div className='winner-spotlight__details'>
                  <div className='winner-spotlight__details-row'>
                    <h3 className='winner-spotlight__name'>{getWinnerName(winner)}</h3>
                    <span className='winner-spotlight__race'>{raceLabel}</span>
                  </div>
                  <p className='winner-spotlight__date'>{winDate}</p>
                  <p className='winner-spotlight__threshold'>
                    Ring Threshold: <strong>{formatPoints(getNumber(threshold))}</strong>
                  </p>
                  <p className='winner-spotlight__bio'>{preview}</p>
                </div>
              </div>
              <span className='winner-spotlight__cta'>Read bio</span>
            </button>
          )
        })}
      </div>

      <MDBModal open={!!activeWinner} onClose={handleClose} className='winner-spotlight__modal-dialog'>
        <MDBModalDialog centered size='lg'>
          <MDBModalContent>
            <MDBModalHeader>
              {activeWinner?.raceLabel} · {getWinnerName(activeWinner?.winner)}
              <MDBBtn type='button' className='btn-close' color='none' onClick={handleClose}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              {activeWinner && (
                <div className='winner-spotlight__modal'>
                  <div className='winner-spotlight__modal-hero'>
                    <img
                      src={activeWinner.headshot}
                      alt={getWinnerName(activeWinner.winner)}
                      className='winner-spotlight__modal-avatar'
                    />
                    <div>
                      <h3 className='winner-spotlight__modal-name'>{getWinnerName(activeWinner.winner)}</h3>
                      <p className='winner-spotlight__modal-meta'>Won on {activeWinner.winDate}</p>
                      <p className='winner-spotlight__modal-meta'>Threshold: {formatPoints(activeWinner.threshold)}</p>
                    </div>
                  </div>
                  <div className='winner-spotlight__modal-bio'>
                    <p>{activeWinner.fullBio}</p>
                  </div>
                </div>
              )}
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn type='button' color='secondary' onClick={handleClose}>
                Close
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </section>
  )
}

export default observer(WinnerSpotlight)
